import { useData } from '@/hooks/useData';
import React, { useEffect, useState, useCallback } from 'react';
import { updateFranchiseConfiguration } from '../../../../../utils/api';

// --- Component Imports ---
import AccordionBasic from '../../../../../components/accordions/AccordionBasic';
import StandardButton from '../../../../../components/buttons/StandardButton';

// --- Type Definitions ---

// DriveThru specific Region type
type DriveThruRegion = {
  key: string | null;
  displayName: string;
  target: number | string;
};

// Lobby specific Region type (aligned with the provided example)
type LobbyRegion = {
    key: string;
    displayName: string;
    target: number | string;
};

// Editable version of the Lobby Region
type EditableLobbyRegion = {
    key: string; // Key will always be managed to match the array index
    displayName: string;
    target: string;
};

// Editable version of the DriveThru Region
type EditableDriveThruRegion = {
  key: string;
  displayName: string;
  target: string;
};

type LobbyConfigurationType = {
  regions: LobbyRegion[];
};

type DriveThruConfigurationType = {
  regions: DriveThruRegion[];
};

// Updated FranchiseConfiguration to include both
type FranchiseConfiguration = {
  lobbyConfiguration?: LobbyConfigurationType;
  driveThruConfiguration?: DriveThruConfigurationType;
} | null | undefined;


const RegionsConfigurationNew: React.FC = () => {
  const [editableLobbyRegions, setEditableLobbyRegions] = useState<EditableLobbyRegion[]>([]);
  const [editableDriveThruRegions, setEditableDriveThruRegions] = useState<EditableDriveThruRegion[]>([]);
  // --- STATE FOR FEEDBACK ---
  const [feedbackMessage, setFeedbackMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const { franchiseConfiguration, setFranchiseConfiguration, selectedFranchise } = useData();

  // --- LOBBY TRANSFORMERS (Exact logic from example) ---
  const transformToEditableLobby = useCallback((regions: LobbyRegion[] | undefined): EditableLobbyRegion[] => {
    if (!Array.isArray(regions)) {
      return [];
    }
    // Ignore incoming keys and generate new ones based on the index.
    return regions.map((region, index) => ({
      key: String(index),
      displayName: region.displayName,
      target: String(region.target),
    }));
  }, []);

  const transformFromEditableLobby = useCallback((editable: EditableLobbyRegion[]): LobbyRegion[] => {
    return editable.map(region => ({
      key: region.key, // Save the index-based key
      displayName: region.displayName,
      target: isNaN(Number(region.target)) ? region.target : Number(region.target),
    }));
  }, []);


  // --- DRIVE THRU TRANSFORMERS (Unaffected) ---
  const transformToEditableDriveThru = useCallback((regions: DriveThruRegion[] | undefined): EditableDriveThruRegion[] => {
    if (!Array.isArray(regions)) {
      return [];
    }
    return regions.map((region, index) => ({
      key: region.key ?? String(index),
      displayName: region.displayName,
      target: String(region.target),
    }));
  }, []);

  const transformFromEditableDriveThru = useCallback((editable: EditableDriveThruRegion[]): DriveThruRegion[] => {
    return editable.map(region => ({
      key: region.key,
      displayName: region.displayName,
      target: isNaN(Number(region.target)) ? region.target : Number(region.target),
    }));
  }, []);


  useEffect(() => {
    if (franchiseConfiguration?.lobbyConfiguration?.regions) {
      setEditableLobbyRegions(transformToEditableLobby(franchiseConfiguration.lobbyConfiguration.regions));
    } else {
      setEditableLobbyRegions([]);
    }

    if (franchiseConfiguration?.driveThruConfiguration?.regions) {
      setEditableDriveThruRegions(transformToEditableDriveThru(franchiseConfiguration.driveThruConfiguration.regions));
    } else {
      setEditableDriveThruRegions([]);
    }
  }, [franchiseConfiguration, transformToEditableLobby, transformToEditableDriveThru]);

  useEffect(() => {
    if (feedbackMessage) {
      const timer = setTimeout(() => {
        setFeedbackMessage(null);
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [feedbackMessage]);

  // --- LOBBY HANDLER (Using index as per example) ---
  const handleLobbyDisplayNameChange = (indexToUpdate: number, newDisplayName: string) => {
    setEditableLobbyRegions(currentRegions =>
      currentRegions.map((region, index) =>
        index === indexToUpdate ? { ...region, displayName: newDisplayName } : region
      )
    );
  };

  // --- DRIVE THRU HANDLER (Unaffected) ---
  const handleDriveThruDisplayNameChange = (key: string, newDisplayName: string) => {
    setEditableDriveThruRegions(currentRegions =>
      currentRegions.map(region =>
        region.key === key ? { ...region, displayName: newDisplayName } : region
      )
    );
  };

  const handleSave = useCallback(() => {
    if (!selectedFranchise) {
      console.error("Cannot save: franchise is missing.");
      return;
    }
    setFeedbackMessage(null);

    const updatedConfig: FranchiseConfiguration = {
      ...franchiseConfiguration,
      lobbyConfiguration: {
        ...(franchiseConfiguration?.lobbyConfiguration || {}),
        regions: transformFromEditableLobby(editableLobbyRegions),
      },
      driveThruConfiguration: {
        ...(franchiseConfiguration?.driveThruConfiguration || {}),
        regions: transformFromEditableDriveThru(editableDriveThruRegions),
      },
    };

    updateFranchiseConfiguration(selectedFranchise.id, updatedConfig)
      .then((success) => {
        if (success) {
          setFranchiseConfiguration(updatedConfig);
          setFeedbackMessage({ type: 'success', message: 'Updates saved successfully!' });
        } else {
          setFeedbackMessage({ type: 'error', message: 'Failed to save updates.' });
        }
      })
      .catch((error) => {
        console.error(`Error updating regions configuration for ${selectedFranchise.id}:`, error);
        setFeedbackMessage({ type: 'error', message: 'An error occurred while saving.' });
      });
  }, [editableLobbyRegions, editableDriveThruRegions, franchiseConfiguration, selectedFranchise, setFranchiseConfiguration, transformFromEditableLobby, transformFromEditableDriveThru]);

  if (!franchiseConfiguration) {
    return <div>Loading Regions Configuration...</div>;
  }

  return (
    <AccordionBasic title="Regions Configuration" dataCy="franchise-regions-accordion">
      <div className="flex flex-col gap-4">

        {/* --- LOBBY SECTION (Updated to use index) --- */}
        <div className="flex flex-col gap-3">
        <div style={{ marginBottom: '16px' }}></div>
        <label>Manage Lobby Regions</label>
          <div
            className="grid items-center gap-x-2 gap-y-3"
            style={{ gridTemplateColumns: 'auto 1fr' }}
          >
            {editableLobbyRegions.map((region, index) => (
              <div key={region.key} className="contents">
                <span className="font-medium text-gray-600 truncate" title={`Region ${index + 1}`}>
                  Region {index + 1}
                </span>

                <input
                    type="text"
                    value={region.displayName}
                    onChange={(e) => handleLobbyDisplayNameChange(index, e.target.value)}
                    placeholder="Display Name"
                    className="flex-grow p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-transparent focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:outline-none"
                    data-cy={`lobby-region-displayName-input-${index}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* --- SEPARATOR --- */}
        <hr className="my-8 border-gray-200 dark:border-gray-700" />


        {/* --- DRIVE THRU SECTION (Unaffected) --- */}
        <div className="flex flex-col gap-3">
            <label>Manage Drive Thru Regions</label>
            <div
                className="grid items-center gap-x-2 gap-y-3"
                style={{ gridTemplateColumns: 'auto 1fr' }}
            >
                {editableDriveThruRegions.map((region, index) => (
                <div key={region.key} className="contents">
                    <span className="font-medium text-gray-600 truncate" title={region.key}>
                    {region.key}
                    </span>

                    <input
                        type="text"
                        value={region.displayName}
                        onChange={(e) => handleDriveThruDisplayNameChange(region.key, e.target.value)}
                        placeholder="Display Name"
                        className="flex-grow p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-transparent focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:outline-none"
                        data-cy={`driveThru-region-displayName-input-${index}`}
                    />
                </div>
                ))}
            </div>
        </div>

        {/* --- UNIFIED SAVE BUTTON & FEEDBACK --- */}
        <div className="flex items-center gap-4 mt-6">
            <StandardButton onClick={handleSave} dataCy="save-regions-configuration-button">
              Save Updates
            </StandardButton>
            {feedbackMessage && (
              <span className={`text-lg transition-opacity duration-900 ${feedbackMessage.type === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                {feedbackMessage.message}
              </span>
            )}
        </div>
      </div>
    </AccordionBasic>
  );
};

export default RegionsConfigurationNew;